import * as pc from 'playcanvas'
import { loadRemotePlayCanvasLod } from './playcanvas-lod-streaming.js'

const DEFAULT_READY_TIMEOUT_MS = 45_000
const DEFAULT_LOD_DISTANCES = [5, 7, 12, 25, 75, 120, 200]
const DEFAULT_SPLAT_BUDGET = 2_000_000
const DEFAULT_LOD_UNDERFILL_LIMIT = 5
const DEFAULT_LOD_UPDATE_ANGLE = 90
const DEFAULT_LOD_BEHIND_PENALTY = 5
const DEFAULT_LOD_UPDATE_DISTANCE = 0.5
const INTERACTIVE_SORT_INTERVAL_MS = 120
const SORT_CAMERA_EPSILON = 0.001

const isLoopbackHostname = hostname => {
  const normalized = String(hostname || '').trim().toLowerCase()
  return normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '::1' || normalized === '[::1]'
}

const joinUrlPath = (prefix, pathname) => {
  const normalizedPrefix = String(prefix || '').replace(/\/+$/, '')
  const normalizedPath = `/${String(pathname || '').replace(/^\/+/, '')}`
  if (!normalizedPrefix || normalizedPrefix === '/') return normalizedPath
  if (normalizedPath === normalizedPrefix || normalizedPath.startsWith(`${normalizedPrefix}/`)) return normalizedPath
  return `${normalizedPrefix}${normalizedPath}`
}

/** Keep signed LOD resources on the browser-facing API origin used by the viewer. */
export const resolveRemoteLodManifestUrl = (value, apiBaseUrl = '/backend') => {
  const rawValue = String(value || '').trim()
  if (!rawValue) throw new Error('PlayCanvas LOD manifest URL is missing')
  if (typeof window === 'undefined') return rawValue

  const manifest = new URL(rawValue, window.location.href)
  const publicApi = new URL(apiBaseUrl || '/backend', window.location.origin)
  if (manifest.origin === publicApi.origin) return manifest.toString()

  const isWorkModelLodAsset = /\/api\/assets\/work-model\/[^/]+\/lod\//i.test(manifest.pathname)
  const isOfficialModelLodAsset = /\/api\/assets\/official-model\/[^/]+\/lod\//i.test(manifest.pathname)
  const shouldRebase = isWorkModelLodAsset ||
    isOfficialModelLodAsset ||
    (isLoopbackHostname(manifest.hostname) && !isLoopbackHostname(window.location.hostname))
  if (!shouldRebase) return manifest.toString()

  manifest.protocol = publicApi.protocol
  manifest.host = publicApi.host
  manifest.pathname = joinUrlPath(publicApi.pathname, manifest.pathname)
  return manifest.toString()
}

const toFiniteVec3 = value => {
  const x = Number(value?.x)
  const y = Number(value?.y)
  const z = Number(value?.z)
  return Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)
    ? new pc.Vec3(x, y, z)
    : null
}

const applyInitialCamera = (entity, cameraControls, initialCameraPose) => {
  const focus = toFiniteVec3(initialCameraPose?.focus)
  const cameraPosition = toFiniteVec3(initialCameraPose?.cameraPosition || initialCameraPose?.position)
  if (focus && cameraPosition && cameraControls?.reset) {
    return cameraControls.reset(focus, cameraPosition, { immediate: true })
  }
  return cameraControls?.focusOnEntity?.(entity) ?? null
}

const getRenderedSplatCount = app => {
  const count = Number(app.stats?.frame?.gsplats || 0)
  return Number.isFinite(count) && count > 0 ? count : 0
}

const isFiniteVec3 = value => (
  Number.isFinite(value.x) && Number.isFinite(value.y) && Number.isFinite(value.z)
)

/** Keep camera interaction responsive without delaying a changed LOD world-state. */
const installInteractiveSortThrottle = (app, cameraComponent) => {
  const director = app.renderer?.gsplatDirector
  const cameraData = cameraComponent?.camera
    ? director?.camerasMap?.get(cameraComponent.camera)
    : null
  const managers = new Set()
  for (const layerData of cameraData?.layersMap?.values() || []) {
    if (layerData.gsplatManager) managers.add(layerData.gsplatManager)
    if (layerData.gsplatManagerShadow) managers.add(layerData.gsplatManagerShadow)
  }

  const restores = []
  for (const manager of managers) {
    const originalTestCameraMovedForSort = manager.testCameraMovedForSort
    if (typeof originalTestCameraMovedForSort === 'function') {
      let lastSortRequestAt = Number.NEGATIVE_INFINITY
      manager.testCameraMovedForSort = () => {
        if (!originalTestCameraMovedForSort.call(manager)) return false
        const now = performance.now()
        if (now - lastSortRequestAt < INTERACTIVE_SORT_INTERVAL_MS) return false
        lastSortRequestAt = now
        return true
      }
      restores.push(() => {
        manager.testCameraMovedForSort = originalTestCameraMovedForSort
      })
    }

    const originalSortGpuHybrid = manager.sortGpuHybrid
    if (typeof originalSortGpuHybrid === 'function') {
      let lastSortAt = Number.NEGATIVE_INFINITY
      let lastWorldStateVersion = Symbol('initial-world-state')
      const lastCameraPosition = new pc.Vec3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY)
      const lastCameraForward = new pc.Vec3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY)
      manager.sortGpuHybrid = worldState => {
        const cameraPosition = manager.cameraNode?.getPosition?.()
        const cameraForward = manager.cameraNode?.forward
        const radialSorting = manager.scene?.gsplat?.radialSorting === true
        const cameraChanged = !cameraPosition || !cameraForward || !isFiniteVec3(lastCameraPosition) ||
          (radialSorting
            ? lastCameraPosition.distance(cameraPosition) > SORT_CAMERA_EPSILON
            : Math.acos(Math.min(1, Math.max(-1, lastCameraForward.dot(cameraForward)))) > SORT_CAMERA_EPSILON)
        const stateChanged = worldState?.version !== lastWorldStateVersion ||
          Number(manager.sortedVersion) !== Number(worldState?.version)
        const now = performance.now()
        if (!stateChanged && (!cameraChanged || now - lastSortAt < INTERACTIVE_SORT_INTERVAL_MS)) {
          return undefined
        }
        const result = originalSortGpuHybrid.call(manager, worldState)
        lastSortAt = now
        lastWorldStateVersion = worldState?.version
        if (cameraPosition) lastCameraPosition.copy(cameraPosition)
        if (cameraForward) lastCameraForward.copy(cameraForward)
        return result
      }
      restores.push(() => {
        manager.sortGpuHybrid = originalSortGpuHybrid
      })
    }
  }

  return () => restores.splice(0).reverse().forEach(restore => restore())
}

const collectCoarsestLodFileIndices = octree => {
  const lodLevels = Math.floor(Number(octree?.lodLevels) || 0)
  if (lodLevels < 1 || !Array.isArray(octree?.nodes) || octree.nodes.length === 0) {
    throw new Error('PlayCanvas LOD octree has no coarsest level nodes')
  }

  const coarsestLod = lodLevels - 1
  const fileIndices = new Set()
  for (const node of octree.nodes) {
    const value = node?.lods?.[coarsestLod]?.fileIndex
    if (value === undefined || value === null || value === -1) continue
    const fileIndex = Number(value)
    if (!Number.isSafeInteger(fileIndex) || fileIndex < 0 ||
      (Array.isArray(octree.files) && fileIndex >= octree.files.length)) {
      throw new Error('PlayCanvas LOD coarsest level contains an invalid file reference')
    }
    fileIndices.add(fileIndex)
  }

  if (fileIndices.size === 0) {
    throw new Error('PlayCanvas LOD coarsest level has no file resources')
  }
  return Array.from(fileIndices)
}

/** Load and decode the complete coarse LOD before mounting an entity into the scene. */
const preloadCompleteCoarsestLod = ({ octree, timeoutMs, onProgress }) => new Promise((resolve, reject) => {
  if (typeof octree?.ensureFileResource !== 'function' || !(octree.fileResources instanceof Map)) {
    reject(new Error('PlayCanvas LOD octree does not support file preloading'))
    return
  }

  let fileIndices
  try {
    fileIndices = collectCoarsestLodFileIndices(octree)
  } catch (error) {
    reject(error)
    return
  }

  let settled = false
  let loadedCount = 0
  let reportedLoadedCount = -1
  let pollTimer = null
  let stallTimer = null

  const cleanup = () => {
    if (pollTimer !== null) clearTimeout(pollTimer)
    if (stallTimer !== null) clearTimeout(stallTimer)
  }
  const finish = error => {
    if (settled) return
    settled = true
    cleanup()
    if (error) reject(error)
    else resolve({ fileIndices })
  }
  const resetStallTimer = () => {
    if (stallTimer !== null) clearTimeout(stallTimer)
    stallTimer = setTimeout(() => {
      finish(new Error(
        `PlayCanvas complete coarsest LOD stalled (${loadedCount}/${fileIndices.length} blocks loaded)`
      ))
    }, timeoutMs)
  }
  const poll = () => {
    try {
      for (const fileIndex of fileIndices) octree.ensureFileResource(fileIndex)
      loadedCount = fileIndices.reduce(
        (count, fileIndex) => count + (octree.fileResources.has(fileIndex) ? 1 : 0),
        0
      )
      const loadingCount = fileIndices.length - loadedCount
      if (loadedCount !== reportedLoadedCount) {
        reportedLoadedCount = loadedCount
        resetStallTimer()
        const progress = 20 + Math.round((loadedCount / fileIndices.length) * 70)
        onProgress?.({ phase: 'streaming', progress, loadingCount, renderedSplats: 0 })
      }
      if (loadingCount === 0) {
        finish()
        return
      }
      pollTimer = setTimeout(poll, 50)
    } catch (error) {
      finish(error instanceof Error ? error : new Error(String(error)))
    }
  }

  resetStallTimer()
  poll()
})

/**
 * Match the PlayCanvas LOD streaming example: render the coarsest level first, then open the full
 * LOD range while retaining loaded coarse nodes until their finer replacements are ready.
 */
const configureProgressiveLod = (app, lodLevelsValue) => {
  const settings = app.scene.gsplat
  const lodLevels = Math.max(1, Math.floor(Number(lodLevelsValue) || 1))
  const coarsestLod = lodLevels - 1
  const previous = {
    radialSorting: settings.radialSorting,
    lodUpdateAngle: settings.lodUpdateAngle,
    lodBehindPenalty: settings.lodBehindPenalty,
    lodUpdateDistance: settings.lodUpdateDistance,
    lodUnderfillLimit: settings.lodUnderfillLimit,
    lodRangeMin: settings.lodRangeMin,
    lodRangeMax: settings.lodRangeMax
  }

  settings.radialSorting = true
  settings.lodUpdateAngle = DEFAULT_LOD_UPDATE_ANGLE
  settings.lodBehindPenalty = DEFAULT_LOD_BEHIND_PENALTY
  settings.lodUpdateDistance = DEFAULT_LOD_UPDATE_DISTANCE
  settings.lodUnderfillLimit = DEFAULT_LOD_UNDERFILL_LIMIT
  settings.lodRangeMin = coarsestLod
  settings.lodRangeMax = coarsestLod

  let restored = false
  return {
    revealFullRange: () => {
      if (restored) return
      settings.lodRangeMin = 0
      settings.lodRangeMax = coarsestLod
    },
    restore: () => {
      if (restored) return
      restored = true
      settings.radialSorting = previous.radialSorting
      settings.lodUpdateAngle = previous.lodUpdateAngle
      settings.lodBehindPenalty = previous.lodBehindPenalty
      settings.lodUpdateDistance = previous.lodUpdateDistance
      settings.lodUnderfillLimit = previous.lodUnderfillLimit
      settings.lodRangeMin = previous.lodRangeMin
      settings.lodRangeMax = previous.lodRangeMax
    }
  }
}

const waitForCompleteCoarsestLodFrame = ({
  app,
  cameraComponent,
  octree,
  coarsestFileIndices,
  timeoutMs,
  onProgress
}) => new Promise((resolve, reject) => {
  const gsplatSystem = app.systems?.gsplat
  if (!gsplatSystem) {
    reject(new Error('PlayCanvas GSplat system is unavailable'))
    return
  }

  let settled = false
  let readyCandidateScheduled = false
  let readyFrameRequest = null

  const cleanup = () => {
    gsplatSystem.off('frame:ready', onFrameReady)
    if (readyFrameRequest !== null) cancelAnimationFrame(readyFrameRequest)
    clearTimeout(timer)
  }
  const finish = error => {
    if (settled) return
    settled = true
    cleanup()
    if (error) reject(error)
    else resolve()
  }
  const checkReadyCandidate = () => {
    readyCandidateScheduled = false
    readyFrameRequest = null
    const renderedSplats = getRenderedSplatCount(app)
    const hasCompleteCoarsestLod = coarsestFileIndices.every(fileIndex => octree.fileResources?.has(fileIndex))
    if (!hasCompleteCoarsestLod || renderedSplats <= 0) return
    onProgress?.({ phase: 'ready', progress: 100, loadingCount: 0, renderedSplats })
    finish()
  }
  const onFrameReady = (eventCamera, _layer, ready, loadingCountValue) => {
    if (cameraComponent && eventCamera !== cameraComponent) return
    const loadingCount = Math.max(0, Number(loadingCountValue) || 0)
    const renderedSplats = getRenderedSplatCount(app)
    const progress = renderedSplats > 0 ? 95 : 90
    onProgress?.({ phase: 'streaming', progress, loadingCount, renderedSplats })

    // Every coarse SOG resource was loaded before mounting. Wait until PlayCanvas reconciles those
    // resources into a stable rendered frame, then expose finer LODs.
    const hasCompleteCoarsestLod = coarsestFileIndices.every(fileIndex => octree.fileResources?.has(fileIndex))
    if (ready && hasCompleteCoarsestLod && renderedSplats > 0 && !readyCandidateScheduled) {
      readyCandidateScheduled = true
      readyFrameRequest = requestAnimationFrame(checkReadyCandidate)
    }
  }

  const timer = setTimeout(() => {
    finish(new Error('PlayCanvas complete coarsest LOD frame timed out; check node asset requests'))
  }, timeoutMs)

  gsplatSystem.on('frame:ready', onFrameReady)
})

export async function renderRemotePlayCanvasLod(options) {
  const {
    app,
    cameraComponent = null,
    cameraControls = null,
    initialCameraPose = null,
    entityName = 'remote-lod-splat',
    lodDistances = DEFAULT_LOD_DISTANCES,
    splatBudget = DEFAULT_SPLAT_BUDGET,
    readyTimeoutMs = DEFAULT_READY_TIMEOUT_MS,
    onProgress
  } = options

  if (!app?.root || !app?.assets) throw new Error('PlayCanvas application is unavailable')
  const manifestUrl = resolveRemoteLodManifestUrl(options.manifestUrl, options.apiBaseUrl)
  onProgress?.({ phase: 'manifest', progress: 0, loadingCount: 0, renderedSplats: 0 })

  const asset = await loadRemotePlayCanvasLod(app.assets, manifestUrl)
  const resource = asset.resource
  if (!resource?.octree || !resource?.aabb) {
    app.assets.remove(asset)
    asset.unload()
    throw new Error('PlayCanvas did not return a valid LOD octree resource')
  }

  const entity = new pc.Entity(entityName)
  const progressiveLod = configureProgressiveLod(app, resource.octree.lodLevels)
  let cameraPose
  let restoreSortThrottle = () => {}

  try {
    onProgress?.({ phase: 'mounting', progress: 15, loadingCount: 0, renderedSplats: 0 })
    const { fileIndices: coarsestFileIndices } = await preloadCompleteCoarsestLod({
      octree: resource.octree,
      timeoutMs: readyTimeoutMs,
      onProgress
    })

    // Create the enabled component only after every coarse file is ready. Until this point there
    // is no entity in the hierarchy, so a partially loaded spatial subset cannot become visible.
    entity.addComponent('gsplat', {
      asset,
      unified: true,
      lodDistances,
      splatBudget
    })
    entity.setLocalPosition(0, 0, 0)
    entity.setLocalEulerAngles(0, 0, 0)
    entity.setLocalScale(1, 1, 1)

    const completeCoarsestFramePromise = waitForCompleteCoarsestLodFrame({
      app,
      cameraComponent,
      octree: resource.octree,
      coarsestFileIndices,
      timeoutMs: readyTimeoutMs,
      onProgress
    })
    app.root.addChild(entity)
    cameraPose = applyInitialCamera(entity, cameraControls, initialCameraPose)
    await completeCoarsestFramePromise
    restoreSortThrottle = installInteractiveSortThrottle(app, cameraComponent)
    progressiveLod.revealFullRange()
  } catch (error) {
    entity.destroy()
    app.assets.remove(asset)
    asset.unload()
    restoreSortThrottle()
    progressiveLod.restore()
    throw error
  }

  let destroyed = false
  return {
    asset,
    entity,
    manifestUrl,
    cameraPose,
    destroy: () => {
      if (destroyed) return
      destroyed = true
      entity.destroy()
      app.assets.remove(asset)
      asset.unload()
      restoreSortThrottle()
      progressiveLod.restore()
    }
  }
}

export const destroyRemotePlayCanvasLod = session => {
  session?.destroy()
}
